import React, { forwardRef } from 'react';
import { TreeList, Column, Selection, SearchPanel } from 'devextreme-react/tree-list';
import { LoadPanel } from 'devextreme-react/load-panel';
import { withStyles } from '@material-ui/core';
import styles from './CatalogueEntries.styles';
import { DATA_GRID_LOADING_INDICATOR_HEIGHT_WIDTH } from 'components/common/DataGridDevEx/DataGrid.constants';
import { ICatalogueEntriesHandle, ICatalogueEntriesProps, IReportingFacadeExtended } from './CatalogueEntries.properties';
import { useVSDispatch, useVRContextSelector } from '../VehicleSearch/VehicleSearchContext';
import { VehicleActionTypes } from '../VehicleSearch/VehicleSearch.actions';

const MAX_LEVEL = 3;

const getNodeByLevel = (node, level) => {
  if (!node.parent) {
    return;
  }

  if (node.parent.level === level || node.parent.level === undefined) {
    return node.parent;
  } else {
    return getNodeByLevel(node.parent, level);
  }
};

const modifyEmptySpaceStyles = (e) => {
  const currentNode = e.row.node;
  const $emptySpaceElements = e.cellElement.querySelectorAll(
    '.dx-treelist-empty-space'
  );
  const children = currentNode.parent.children;
  const isLasChildren = children[children.length - 1].key === currentNode.key;

  for (let i = 0; i < $emptySpaceElements.length; i++) {
    const node = getNodeByLevel(currentNode, i - 1);

    if (
      node &&
      node.children.length > 1 &&
      currentNode.parent.key !== node.children[node.children.length - 1].key
    ) {
      $emptySpaceElements[i].classList.add('dx-line');
    }

    if (i === $emptySpaceElements.length - 1) {
      $emptySpaceElements[i].classList.add('dx-line');
      $emptySpaceElements[i].classList.add('dx-line-middle');
      if (isLasChildren) {
        $emptySpaceElements[i].classList.toggle('dx-line-last');
      }
    }
  }
};

const CatalogueEntries = (props: ICatalogueEntriesProps, ref: React.Ref<ICatalogueEntriesHandle>) => {
  const { classes, catalogueEntriesData, isLoading } = props;
  const treeListRef = React.useRef<TreeList>();
  const contextDispatch = useVSDispatch();
  const selectedCatalogueEntry = useVRContextSelector<'SelectedCatalogueEntry'>((state) => state.SelectedCatalogueEntry);

  const [searchValue, setSearchValue] = React.useState('');
  const shouldRestoreSelectionRef = React.useRef(false);

  // Set flag when catalogueEntriesData changes to trigger selection restoration
  React.useEffect(
    () => {
      if (catalogueEntriesData) {
        shouldRestoreSelectionRef.current = true;
      }
    },
    [catalogueEntriesData]);

  const filterAreaItems = React.useCallback(
    (Catalogues: any, parentId: number, level: number = 1): IReportingFacadeExtended[] => {
      return Catalogues
        ?.filter((r) => r.ParentID === parentId)
        ?.filter(() => {
          // Hide 3rd level unless searching
          if (level >= MAX_LEVEL && !searchValue) {
            return false;
          }

          return true;
        })
        ?.map((area) => {
          area.items = filterAreaItems(Catalogues, area?.ItemID, level + 1);

          return area;
        }) || [];
    },
    [searchValue]
  );

  const dataSource = React.useMemo(
    () => ({
      store: filterAreaItems(catalogueEntriesData?.Catalogues, 0),
    }),
    [catalogueEntriesData, filterAreaItems]
  );

  const onCellPrepared = React.useCallback(
    (e) => {
      if (e.rowType === 'data' && e.columnIndex === 0) {
        modifyEmptySpaceStyles(e);
      }
    },
    []
  );

  React.useImperativeHandle(
    ref,
    () => ({
      OnCollapseAll: () => {
        const parent = catalogueEntriesData?.Catalogues?.find((r) => r?.IsPrimary);
        treeListRef.current?.instance.collapseRow(parent?.ItemID);
      }
    }),
    [catalogueEntriesData]
  );

  const handleOptionChanged = React.useCallback(
    (e) => {
      if (e.fullName === 'searchPanel.text') {
        setSearchValue(e.value);
      }
    },
    []
  );

  return (
    <div className={classes.TreeListContainer}>
      <span className={classes.TreeListTitle}>Catalogue Entries</span>
      <TreeList
        ref={treeListRef}
        className={classes.CatalogueEntriesTreeList}
        dataSource={dataSource || []}
        columnAutoWidth={true}
        itemsExpr='items'
        keyExpr='ItemID'
        parentIdExpr='ParentID'
        dataStructure='tree'
        showColumnHeaders={false}
        showRowLines={false}
        showBorders={true}
        focusedRowEnabled={true}
        onCellPrepared={onCellPrepared}
        onOptionChanged={handleOptionChanged}
        noDataText=''
        onRowClick={(e) => {
          contextDispatch({
            [VehicleActionTypes.setSelectedCatalogueEntry]: e.data,
          });
        }}
        onContentReady={(e) => {
          // If we should restore selection, use context
          if (shouldRestoreSelectionRef.current && selectedCatalogueEntry && selectedCatalogueEntry.ItemID) {
            const nodeByKey = e.component.getNodeByKey(selectedCatalogueEntry.ItemID);

            if (nodeByKey) {
              // Expand parent nodes to make the selected node visible
              let parentNode = nodeByKey.parent;
              while (parentNode) {
                e.component.expandRow(parentNode.key);
                parentNode = parentNode.parent;
              }

              // Select and focus the previously selected row
              e.component.selectRows([selectedCatalogueEntry.ItemID], false);
              e.component.option('focusedRowKey', selectedCatalogueEntry.ItemID);
              shouldRestoreSelectionRef.current = false; // Mark as restored

              return; // Exit early
            }
          }

          // Reset the flag since we couldn't restore or didn't need to
          shouldRestoreSelectionRef.current = false;

          // Only select first row if no row is selected AND no context selection exists
          if (e.component.getSelectedRowKeys().length === 0 && !selectedCatalogueEntry) {
            const firstRowKey = e.component.getKeyByRowIndex(0);
            if (firstRowKey) {
              e.component.selectRows([firstRowKey], false);
              e.component.option('focusedRowKey', firstRowKey);

              const firstRowData = e.component.getVisibleRows()[0]?.data;
              if (firstRowData) {
                contextDispatch({
                  [VehicleActionTypes.setSelectedCatalogueEntry]: firstRowData,
                });
              }
            }
          }
        }}
      >
        <Selection mode='single' />
        <Column dataField='Description' />
        <SearchPanel visible={true} highlightCaseSensitive={false} />
      </TreeList>

      <LoadPanel
        visible={isLoading}
        shading={true}
        showIndicator={true}
        showPane={true}
        position={{ of: treeListRef.current?.instance?.element() }}
        height={DATA_GRID_LOADING_INDICATOR_HEIGHT_WIDTH}
        width={DATA_GRID_LOADING_INDICATOR_HEIGHT_WIDTH}
      />
    </div>
  );
};

export default withStyles(styles, { index: 1 })(forwardRef(CatalogueEntries));
